import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PaginationParams } from '../types/common';
import { CreateTrainingTopicDto, UpdateTrainingTopicDto } from '../types/dtos/training';
import { createTrainingTopicService } from '../services/training-topic.service';

const TRAINING_TOPICS_KEY = 'training-topics';

export const useTrainingTopics = (
  organizationId: string, 
  trainingId: string, 
  moduleId: string,
  params?: PaginationParams
) => {
  const topicService = createTrainingTopicService(organizationId, trainingId, moduleId);

  return useQuery({
    queryKey: [TRAINING_TOPICS_KEY, organizationId, trainingId, moduleId, params],
    queryFn: () => topicService.findAll(params).then((res) => res.data),
    enabled: !!organizationId && !!trainingId && !!moduleId
  });
};

export const useTrainingTopic = (
  organizationId: string, 
  trainingId: string, 
  moduleId: string,
  topicId: string
) => {
  const topicService = createTrainingTopicService(organizationId, trainingId, moduleId);

  return useQuery({
    queryKey: [TRAINING_TOPICS_KEY, organizationId, trainingId, moduleId, topicId],
    queryFn: () => topicService.findById(topicId).then((res) => res.data),
    enabled: !!organizationId && !!trainingId && !!moduleId && !!topicId
  });
};

export const useCreateTrainingTopic = (
  organizationId: string, 
  trainingId: string, 
  moduleId: string
) => {
  const queryClient = useQueryClient();
  const topicService = createTrainingTopicService(organizationId, trainingId, moduleId);

  return useMutation({
    mutationFn: (data: CreateTrainingTopicDto) => 
      topicService.create(data).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TRAINING_TOPICS_KEY, organizationId, trainingId, moduleId] });
    }
  });
};

export const useUpdateTrainingTopic = (
  organizationId: string, 
  trainingId: string, 
  moduleId: string
) => {
  const queryClient = useQueryClient();
  const topicService = createTrainingTopicService(organizationId, trainingId, moduleId);

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTrainingTopicDto }) =>
      topicService.update(id, data).then((res) => res.data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [TRAINING_TOPICS_KEY, organizationId, trainingId, moduleId] });
      queryClient.invalidateQueries({ queryKey: [TRAINING_TOPICS_KEY, organizationId, trainingId, moduleId, id] });
    }
  });
};

export const useDeleteTrainingTopic = (
  organizationId: string, 
  trainingId: string, 
  moduleId: string
) => {
  const queryClient = useQueryClient();
  const topicService = createTrainingTopicService(organizationId, trainingId, moduleId);

  return useMutation({
    mutationFn: (id: string) => topicService.delete(id).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TRAINING_TOPICS_KEY, organizationId, trainingId, moduleId] });
    }
  });
};

